import React, { useState, useEffect } from 'react';
import { X, User, DollarSign, FileText, Calendar } from 'lucide-react';

interface Coordinator {
  id: string;
  coordinatorId: string;
  coordinatorName: string;
  coordinatorPic: string;
  level: number;
}

interface PayoutAmount {
  title: string;
  amount: number;
  editable: boolean;
}

interface AddExpenseDialogProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  slotId: string;
  inventoryId: string;
  tripTitle: string;
  startTime: number;
  endTime: number;
  coordinators: Coordinator[];
  onClose: () => void;
  onSubmit: (expenseData: any) => void;
}

const AddExpenseDialog: React.FC<AddExpenseDialogProps> = ({
  open,
  setOpen,
  slotId,
  inventoryId,
  tripTitle,
  startTime,
  endTime,
  coordinators,
  onClose,
  onSubmit
}) => {
  const coordinatorAssigned = coordinators && coordinators?.length > 0;
  const defaultCategory = coordinatorAssigned ? "USER" : "TRIP";
  
  const [title, setTitle] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [paymentMode, setPaymentMode] = useState("undefined");
  const [type, setType] = useState("undefined");
  const [againstUserId, setAgainstUserId] = useState("undefined");
  const [createdUserId, setCreatedUserId] = useState("undefined");
  const [category, setCategory] = useState(defaultCategory);
  const [amountList, setAmountList] = useState<PayoutAmount[]>([]);
  
  const isTrip = category === "TRIP";
  const isUser = category === "USER";

  // Mock inventory data - in real app, this would come from API
  const mockInventoryData = {
    headAmount: [
      { title: "Base Amount", amount: 2000, editable: false },
      { title: "Bonus", amount: 500, editable: true }
    ],
    mainAmount: [
      { title: "Base Amount", amount: 1500, editable: false },
      { title: "Bonus", amount: 300, editable: true }
    ],
    traineeAmount: [
      { title: "Base Amount", amount: 1000, editable: false },
      { title: "Bonus", amount: 200, editable: true }
    ]
  };

  useEffect(() => {
    if (open) {
      setTitle("");
      setAmount("");
      setNote("");
      setPaymentMode("undefined");
      setType("undefined");
      setAgainstUserId("undefined");
      setCreatedUserId("undefined");
      setCategory(defaultCategory);
      setAmountList([]);
    }
  }, [open, defaultCategory]);

  const getCoordinatorLevelById = (coordinatorId: string, coordinators: Coordinator[]) => {
    return coordinators?.find((coordinator) => coordinator.coordinatorId === coordinatorId)?.level || 1;
  };

  const getAmountSum = (amountList: PayoutAmount[]) => {
    return amountList?.reduce((acc, item) => acc + Number(item.amount), 0);
  };

  const getAmountListKeyAmountString = (amountList: PayoutAmount[]) => {
    return amountList?.map((item) => `${item.title}:${item.amount}`).join(", ");
  };

  const updateAmountListByTitle = (amountList: PayoutAmount[], title: string, amount: number) => {
    return amountList?.map((item) => {
      if (item.title === title) {
        return { ...item, amount: amount };
      }
      return item;
    });
  };

  const getPayoutAmountList = (level: number): PayoutAmount[] => {
    if (level === 3) {
      return mockInventoryData.headAmount;
    } else if (level === 2) {
      return mockInventoryData.mainAmount;
    } else if (level === 1) {
      return mockInventoryData.traineeAmount;
    } else {
      return mockInventoryData.traineeAmount;
    }
  };

  const getDesignation = (level: number) => {
    if (level === 3) return 'Head';
    if (level === 2) return 'Main';
    if (level === 1) return 'Trainee';
    return 'NA';
  };

  const tripSubmitEnabled = slotId && inventoryId && title && paymentMode !== "undefined"
    && type !== "undefined" && Number(amount) > 0 && tripTitle && startTime && endTime;

  const userSubmitEnabled = slotId && inventoryId && title
    && againstUserId !== "undefined" && Number(amount) > 0 && tripTitle && startTime && endTime;

  const submitEnabled = isTrip ? tripSubmitEnabled : userSubmitEnabled;

  const handleSubmit = () => {
    if (submitEnabled) {
      const expenseData = {
        source: "admin-app",
        title: title,
        amount: Number(amount) * 100,
        type: isUser ? "OVERDUE" : type,
        transactionMode: isUser ? "NA" : paymentMode,
        note: note,
        slotId: slotId,
        tripTitle: tripTitle,
        slotStartTime: startTime,
        slotEndTime: endTime,
        inventoryId: inventoryId,
        category: category,
        createdUserId: (!isUser && createdUserId !== "undefined") ? createdUserId : "1", // Mock user ID
        ...((isUser && againstUserId) && { againstUserId: againstUserId }),
      };
      
      onSubmit(expenseData);
      setOpen(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  const userDefaultSelectionChanges = () => {
    setTimeout(() => {
      if (coordinatorAssigned && coordinators?.length === 1) {
        setAgainstUserId(coordinators[0]?.coordinatorId);
        const selectedCoordinatorLevel = getCoordinatorLevelById(coordinators[0]?.coordinatorId, coordinators);
        const payoutAmountList = getPayoutAmountList(selectedCoordinatorLevel);
        setAmountList(payoutAmountList);
        setAmount(getAmountSum(payoutAmountList).toString());
        setTitle(`${getDesignation(selectedCoordinatorLevel)}: ${tripTitle} : ${new Date(startTime).toDateString()}`);
        setNote(getAmountListKeyAmountString(payoutAmountList));
      }
    }, 400);
  };

  useEffect(() => {
    if (open && category === "USER") {
      userDefaultSelectionChanges();
    }
  }, [open, category, coordinatorAssigned]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[80vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">Add Expenses</h3>
          <button
            onClick={handleClose}
            className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close dialog"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4 max-h-96 overflow-y-auto space-y-4">
          {/* Category Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
            <select
              value={category}
              onChange={(e) => {
                setCategory(e.target.value);
                if (e.target.value === "USER") {
                  userDefaultSelectionChanges();
                } else {
                  setAmountList([]);
                }
                // Reset form
                setAgainstUserId("undefined");
                setCreatedUserId("undefined");
                setPaymentMode("undefined");
                setType("undefined");
                setTitle("");
                setAmount("");
                setNote("");
              }}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              <option value="TRIP">TRIP</option>
              {coordinatorAssigned && <option value="USER">COORDINATOR</option>}
            </select>
          </div>

          {/* Trip Expense Fields */}
          {isTrip && (
            <>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Payment Mode</label>
                  <select
                    value={paymentMode}
                    onChange={(e) => setPaymentMode(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="undefined">Select Payment Mode</option>
                    <option value="BOZ CARD">BOZ CARD</option>
                    <option value="BOZ CASH">BOZ CASH</option>
                    <option value="BOZ UPI">BOZ UPI</option>
                    <option value="SELF">SELF (Any Mode)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Expense Type</label>
                  <select
                    value={type}
                    onChange={(e) => setType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="undefined">Select Expense Type</option>
                    <option value="BREAKFAST">BREAKFAST</option>
                    <option value="PARKING">PARKING</option>
                    <option value="TICKETS">TICKETS</option>
                    <option value="TOLLS">TOLLS</option>
                    <option value="TRANSPORT">TRANSPORT</option>
                    <option value="HOTEL">HOTEL</option>
                    <option value="COORDINATOR FOOD">COORDINATOR FOOD</option>
                    <option value="OTHERS">OTHERS</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Created By</label>
                  <select
                    value={createdUserId}
                    onChange={(e) => {
                      setCreatedUserId(e.target.value);
                      setPaymentMode("SELF");
                      setNote(`Entry by Admin: ${note}`);
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="undefined">By Self (Logged In)</option>
                    <option value="1">Ashraf</option>
                    <option value="4">Muskan</option>
                    <option value="109">Atif</option>
                    {coordinators?.map((coordinator) => (
                      <option value={coordinator?.coordinatorId} key={coordinator?.id}>
                        {coordinator?.coordinatorName} ({getDesignation(coordinator?.level)})
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </>
          )}

          {/* User Expense Fields */}
          {isUser && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Select Coordinator</label>
              <select
                value={againstUserId}
                onChange={(e) => {
                  setAgainstUserId(e.target.value);
                  const selectedCoordinatorLevel = getCoordinatorLevelById(e.target.value, coordinators);
                  const payoutAmountList = getPayoutAmountList(selectedCoordinatorLevel);
                  setAmountList(payoutAmountList);
                  setAmount(getAmountSum(payoutAmountList).toString());
                  setTitle(`${getDesignation(selectedCoordinatorLevel)}: ${tripTitle} : ${new Date(startTime).toDateString()}`);
                  setNote(getAmountListKeyAmountString(payoutAmountList));
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="undefined">Select Coordinator</option>
                {coordinators?.map((coordinator) => (
                  <option value={coordinator?.coordinatorId} key={coordinator?.id}>
                    {coordinator?.coordinatorName} ({getDesignation(coordinator?.level)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Title</label>
            <input
              type="text"
              placeholder="Title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Amount List for User Expenses */}
          {isUser && amountList?.length > 0 && (
            <div className="space-y-3">
              {amountList?.map((payout) => (
                <div key={payout?.title}>
                  <label className="block text-sm font-medium text-gray-700 mb-2">{payout.title}</label>
                  <input
                    type="number"
                    placeholder={payout.title}
                    value={payout.amount}
                    disabled={!payout.editable}
                    onChange={(e) => {
                      const updatedAmountList = updateAmountListByTitle(amountList, payout.title, Number(e.target.value));
                      setAmountList(updatedAmountList);
                      setAmount(getAmountSum(updatedAmountList).toString());
                      setNote(getAmountListKeyAmountString(updatedAmountList));
                    }}
                    className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                      !payout.editable ? 'bg-gray-100 cursor-not-allowed' : ''
                    }`}
                  />
                </div>
              ))}
            </div>
          )}

          {/* Total Amount */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {isUser && amountList?.length > 0 ? 'Total Amount' : 'Amount'}
            </label>
            <input
              type="number"
              placeholder="Amount"
              value={amount}
              disabled={isUser && amountList?.length > 0}
              onChange={(e) => setAmount(e.target.value)}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                isUser && amountList?.length > 0 ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
          </div>

          {/* Note */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Note (Optional)</label>
            <textarea
              placeholder="Note (Optional)"
              value={note}
              disabled={isUser && amountList?.length > 0}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
              className={`w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 ${
                isUser && amountList?.length > 0 ? 'bg-gray-100 cursor-not-allowed' : ''
              }`}
            />
          </div>

          {/* Submit Button */}
          <button
            onClick={handleSubmit}
            disabled={!submitEnabled}
            className={`w-full px-4 py-2 rounded-lg font-medium transition-colors ${
              submitEnabled
                ? 'bg-primary-600 text-white hover:bg-primary-700'
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
          >
            Submit
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddExpenseDialog;
